import { ScheduleConfigVenueLocationType } from "@iventis/domain-model/model/scheduleConfigVenueLocationType";
import { Header5, sectionalMargin, StyledFieldLabel, labelMargin, formFieldVerticalGap, inputHeight, media, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { v4 as uuid } from "uuid";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Switch, TextField } from "@mui/material";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";

import React, { useMemo as useMeme, useMemo } from "react";
import { EMPTY_GUID, replaceItemInArray } from "@iventis/utilities";
import { DataFieldsChips, DataFieldServicesContext, IDataFieldServices } from "@iventis/datafield-editor";
import { appendClusterToVenueLocationTypes } from "./edit-venue-types-helpers";

export const EditVenueTypesComponent: React.FC<{
    onChange: (venueLocationTypes: ScheduleConfigVenueLocationType[]) => void;
    venueLocationTypes: ScheduleConfigVenueLocationType[];
    allDataFields: DataField[];
    onDataFieldsChanged: (dataFields: DataField[]) => void;
    isPreviouslySaved: boolean;
}> = ({ onChange, venueLocationTypes, allDataFields, onDataFieldsChanged, isPreviouslySaved }) => {
    const translate = useIventisTranslate();

    // Clusters may or may not exist. The only way we can tell is by connecting the "allowedParentType"s together to form a hierarchy, and using the array length.
    const clustersType = venueLocationTypes.length === 3 ? venueLocationTypes.find((t) => t.allowedParentType === undefined || t.allowedParentType === EMPTY_GUID) : undefined;
    const venuesType =
        venueLocationTypes.length === 3
            ? venueLocationTypes.find((t) => t.allowedParentType === clustersType.id)
            : venueLocationTypes.find((t) => t.allowedParentType === undefined || t.allowedParentType === EMPTY_GUID);
    const spacesType = venueLocationTypes.find((t) => t.allowedParentType === venuesType.id);

    const onClustersEnabledChange = (clustersEnabled: boolean) => {
        if (clustersEnabled) {
            if (venueLocationTypes.length === 2) {
                // If we only have two venue location types, add the clusters
                onChange(appendClusterToVenueLocationTypes(venueLocationTypes));
            } else {
                throw new Error("Attempted to enable clusters but they are already enabled");
            }
        } else if (venueLocationTypes.length === 3) {
            // Omit the clusters type since it has just been disabled
            onChange([{ ...venuesType, allowedParentType: undefined }, spacesType]);
        }
    };

    const onTypeChanged = (venueLocationType: ScheduleConfigVenueLocationType) => {
        onChange([...venueLocationTypes.filter((t) => t.id !== venueLocationType.id), venueLocationType]);
    };

    return (
        <>
            <StyledEditVenueTypeComponent
                title={translate(Content.settings.scheduleConfig.clusterVenueLocationTemplateTitle)}
                onChange={onTypeChanged}
                onDatafieldsChange={onDataFieldsChanged}
                venueType={clustersType}
                allDataFields={allDataFields}
                onEnabledChanged={(enabled) => onClustersEnabledChange(enabled)}
                optional
                enableText={translate(Content.settings.scheduleConfig.enableClusters)}
                enabled={venueLocationTypes.length === 3}
                disableToggle={isPreviouslySaved && venueLocationTypes.length === 3}
            />
            <StyledEditVenueTypeComponent
                title={translate(Content.settings.scheduleConfig.mainVenueLocationTemplateTitle)}
                onChange={onTypeChanged}
                onDatafieldsChange={onDataFieldsChanged}
                venueType={venuesType}
                allDataFields={allDataFields}
                enabled
            />
            <StyledEditVenueTypeComponent
                title={translate(Content.settings.scheduleConfig.subVenueLocationTemplateTitle)}
                onChange={onTypeChanged}
                onDatafieldsChange={onDataFieldsChanged}
                venueType={spacesType}
                allDataFields={allDataFields}
                enabled
            />
        </>
    );
};

const EditVenueTypeComponent: React.FC<{
    title: string;
    onChange: (type: ScheduleConfigVenueLocationType) => void;
    onDatafieldsChange: (dataFields: DataField[]) => void;
    venueType: ScheduleConfigVenueLocationType;
    allDataFields: DataField[];
    onEnabledChanged?: (enabled: boolean) => void;
    optional?: boolean;
    enableText?: string;
    enabled?: boolean;
    disableToggle?: boolean;
    className?: string;
}> = ({
    title,
    onChange,
    onDatafieldsChange: outputDataFieldChange,
    venueType,
    allDataFields,
    onEnabledChanged,
    optional,
    enableText,
    enabled,
    className,
    disableToggle = false,
}) => {
    const translate = useIventisTranslate();

    const onTypeNameChanged = (name: string) => {
        onChange({ ...venueType, name });
    };

    const venueTypeDataFields = useMeme(() => allDataFields?.filter((df) => venueType?.dataFields?.includes(df.id)), [allDataFields, venueType?.dataFields]);

    const onDataFieldsChanged = (venueTypeNewDataFields: DataField[]) => {
        const removedDataFields = venueTypeDataFields.filter((previousDf) => !venueTypeNewDataFields.some((newDf) => previousDf.id === newDf.id));
        const venueTypeAllDataFieldsWithIds = venueTypeNewDataFields.map((df) => ({ ...df, id: df.id || uuid() } as DataField));
        onChange({ ...venueType, dataFields: venueTypeAllDataFieldsWithIds.map((df) => df.id) });
        outputDataFieldChange([
            /* Pass up all the existing data fields, with some exceptions... */
            ...allDataFields.filter(
                (dfFromAll) =>
                    /* Don't include removed data fields */
                    !removedDataFields.some((removed) => removed.id === dfFromAll.id) &&
                    /* Don't include data fields which are part of the venue either, since we add those afterwards */
                    !venueTypeAllDataFieldsWithIds.some((venueTypeDataField) => venueTypeDataField.id === dfFromAll.id)
            ),
            /* Include all the data fields which are a part of this venue */
            ...venueTypeAllDataFieldsWithIds,
        ]);
    };

    const dataFieldServices: IDataFieldServices = useMemo(
        () => ({
            dataFieldsService: {
                postDataField: async (dataField) => onDataFieldsChanged([...venueTypeDataFields, dataField]),
                putDataField: async (dataField) => onDataFieldsChanged(replaceItemInArray(venueTypeDataFields, dataField)),
                deleteDataField: async (dataFieldId) => onDataFieldsChanged(venueTypeDataFields.filter(({ id }) => id !== dataFieldId)),
                getDataFieldsForResource: async () => [],
                isDataFieldBeingUsed: async () => true,
            },
            dataFieldListItemsService: {
                postDataFieldListItem: async () => null,
                putDataFieldListItem: async () => null,
                deleteDataFieldListItem: async () => null,
                // List items are not edited here
                getDataFieldListItems: async () => [],
                getDataFieldListItem: async () => null,
            },
        }),
        [onDataFieldsChanged, venueTypeDataFields]
    );

    return (
        <div className={className}>
            <Header5>{title}</Header5>
            {optional && (
                <>
                    {/* Sensible gap so that the switch button looks to align with other page content */}
                    <div style={{ display: "flex", gap: "53px" }}>
                        <StyledFieldLabel style={{ marginBottom: 0, display: "flex", justifyContent: "center", flexDirection: "column" }}>{enableText}</StyledFieldLabel>
                        <Switch color="primary" onChange={(_, checked) => onEnabledChanged(checked)} checked={enabled} disabled={disableToggle} />
                    </div>
                </>
            )}
            {enabled && (
                <div className="inputs">
                    <div>
                        <StyledFieldLabel>{translate(Content.settings.scheduleConfig.venueLocationTypeName)}</StyledFieldLabel>
                        <TextField variant="outlined" onChange={(e) => onTypeNameChanged(e.target.value)} value={venueType?.name} fullWidth />
                    </div>
                    <div>
                        <StyledFieldLabel>{translate(Content.settings.scheduleConfig.attributes)}</StyledFieldLabel>
                        <DataFieldServicesContext.Provider value={dataFieldServices}>
                            <DataFieldsChips
                                className="input-margin-bottom match-input-height"
                                dataFields={venueTypeDataFields || []}
                                getProjectDataFields={() => Promise.resolve([])}
                            />
                        </DataFieldServicesContext.Provider>
                    </div>
                </div>
            )}
        </div>
    );
};

const StyledEditVenueTypeComponent = styled(EditVenueTypeComponent)`
    padding: ${sectionalMargin};

    h5 {
        margin-bottom: ${labelMargin};
    }

    &:not(:last-of-type) {
        border-bottom: 1px solid ${({ theme }: { theme: Theme }) => theme.shades.three};
    }

    .inputs {
        display: flex;
        gap: ${formFieldVerticalGap};

        ${media.extraSmall} {
            flex-direction: column;
        }
    }

    .match-input-height {
        height: ${inputHeight};
        display: flex;
        align-items: center;
    }
`;
